import type { ThreadSyncBackgroundWarmSummary } from "@/lib/thread-sync-background-warm-protocol"
import { compareThreadSyncReplicaSummaries } from "@/lib/thread-sync-replica-arbitration"

export const THREAD_SYNC_REPLICA_METADATA_BROADCAST_CHANNEL =
  "cord-thread-sync-replica-metadata"
const THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX =
  "cord:thread-sync:replica-metadata:"
/**
 * Keep mirrored replica metadata and invalidation tombstones for one day so
 * refreshes and cross-tab handoffs have enough continuity without letting
 * localStorage accumulate stale replica hints indefinitely.
 */
const THREAD_SYNC_REPLICA_METADATA_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Replica metadata writes can happen repeatedly while a hot inactive thread keeps
 * making progress. Pruning once every five minutes keeps stale localStorage keys
 * bounded without scanning all mirrored metadata on every summary update.
 */
const THREAD_SYNC_REPLICA_METADATA_PRUNE_INTERVAL_MS = 5 * 60_000

interface ThreadSyncPersistedReplicaMetadataRecord {
  sessionId: string
  summary: ThreadSyncBackgroundWarmSummary | null
  invalidatedAtMs: number | null
  clearedThroughSavedAtMs: number | null
}

interface ThreadSyncReplicaSummaryUpsertMessage {
  type: "summary_upsert"
  summary: ThreadSyncBackgroundWarmSummary
}

interface ThreadSyncReplicaInvalidationMarkMessage {
  type: "invalidation_mark"
  sessionId: string
  invalidatedAtMs: number
}

interface ThreadSyncReplicaMetadataClearMessage {
  type: "metadata_clear"
  sessionId: string
}

interface ThreadSyncReplicaSummaryClearMessage {
  type: "summary_clear"
  sessionId: string
  clearedThroughSavedAtMs: number | null
}

type ThreadSyncReplicaMetadataMessage =
  | ThreadSyncReplicaSummaryUpsertMessage
  | ThreadSyncReplicaInvalidationMarkMessage
  | ThreadSyncReplicaMetadataClearMessage
  | ThreadSyncReplicaSummaryClearMessage

export type ThreadSyncPersistedReplicaMetadataChange =
  | {
      type: "summary_upsert"
      sessionId: string
    }
  | {
      type: "invalidation_mark"
      sessionId: string
      invalidatedAtMs: number
    }
  | {
      type: "metadata_clear"
      sessionId: string
    }
  | {
      type: "summary_clear"
      sessionId: string
      clearedThroughSavedAtMs: number | null
    }

const persistedReplicaMetadataBySessionId = new Map<
  string,
  ThreadSyncPersistedReplicaMetadataRecord
>()
const subscribers = new Set<
  (change: ThreadSyncPersistedReplicaMetadataChange) => void
>()
const replicaMetadataClockBySessionId = new Map<string, number>()
let broadcastChannel: BroadcastChannel | null = null
let initialized = false
let lastReplicaMetadataPruneAtMs = 0

function areReplicaProvenancesEquivalent(
  left: ThreadSyncBackgroundWarmSummary["provenance"],
  right: ThreadSyncBackgroundWarmSummary["provenance"],
): boolean {
  return (
    left?.sourceKind === right?.sourceKind
    && left?.sourceTabId === right?.sourceTabId
  )
}

function areThreadSyncBackgroundWarmSummariesEquivalent(args: {
  current: ThreadSyncBackgroundWarmSummary
  incoming: ThreadSyncBackgroundWarmSummary
}): boolean {
  return (
    args.current.sessionId === args.incoming.sessionId
    && args.current.agentType === args.incoming.agentType
    && args.current.historyToken === args.incoming.historyToken
    && args.current.storageBackend === args.incoming.storageBackend
    && args.current.cursor === args.incoming.cursor
    && args.current.quality === args.incoming.quality
    && args.current.savedAtMs === args.incoming.savedAtMs
    && areReplicaProvenancesEquivalent(
      args.current.provenance,
      args.incoming.provenance,
    )
  )
}

function areOptionalThreadSyncBackgroundWarmSummariesEquivalent(args: {
  current: ThreadSyncBackgroundWarmSummary | null
  incoming: ThreadSyncBackgroundWarmSummary | null
}): boolean {
  if (args.current === args.incoming) {
    return true
  }

  if (args.current === null || args.incoming === null) {
    return false
  }

  return areThreadSyncBackgroundWarmSummariesEquivalent({
    current: args.current,
    incoming: args.incoming,
  })
}

function normalizeNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function getSummarySavedAtMs(summary: ThreadSyncBackgroundWarmSummary | null): number {
  return typeof summary?.savedAtMs === "number" && Number.isFinite(summary.savedAtMs)
    ? Math.max(0, Math.floor(summary.savedAtMs))
    : 0
}

function observeReplicaMetadataTimestamp(args: {
  sessionId: string
  timestampMs: number | null | undefined
}): void {
  if (
    typeof args.timestampMs !== "number" ||
    !Number.isFinite(args.timestampMs) ||
    args.timestampMs <= 0
  ) {
    return
  }
  replicaMetadataClockBySessionId.set(
    args.sessionId,
    Math.max(
      replicaMetadataClockBySessionId.get(args.sessionId) ?? 0,
      Math.floor(args.timestampMs),
    ),
  )
}

function reserveReplicaSummarySavedAtMs(summary: ThreadSyncBackgroundWarmSummary): ThreadSyncBackgroundWarmSummary {
  const requestedSavedAtMs = getSummarySavedAtMs(summary)
  const existing = getRecord(summary.sessionId)
  const latestFenceMs = existing.invalidatedAtMs ?? 0
  if (latestFenceMs < requestedSavedAtMs) {
    return summary
  }

  const reservedSavedAtMs = Math.max(
    requestedSavedAtMs,
    latestFenceMs + 1,
    (replicaMetadataClockBySessionId.get(summary.sessionId) ?? 0) + 1,
  )
  replicaMetadataClockBySessionId.set(summary.sessionId, reservedSavedAtMs)
  return {
    ...summary,
    savedAtMs: reservedSavedAtMs,
  }
}

function composeReplicaMetadataStorageKey(sessionId: string): string {
  return `${THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX}${encodeURIComponent(sessionId)}`
}

function parseReplicaMetadataStorageKey(key: string): string | null {
  if (!key.startsWith(THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX)) {
    return null
  }

  try {
    return decodeURIComponent(key.slice(THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX.length))
  } catch {
    return null
  }
}

function normalizeSummary(value: unknown): ThreadSyncBackgroundWarmSummary | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, unknown>
  const sessionId = normalizeNonEmptyString(raw.sessionId)
  if (!sessionId) {
    return null
  }

  if (raw.storageBackend !== "durable_replica") {
    return null
  }

  return {
    ...(raw as unknown as ThreadSyncBackgroundWarmSummary),
    sessionId,
    storageBackend: "durable_replica",
  }
}

function normalizeMetadataRecord(
  value: unknown,
): ThreadSyncPersistedReplicaMetadataRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, unknown>
  const sessionId = normalizeNonEmptyString(raw.sessionId)
  if (!sessionId) {
    return null
  }

  const invalidatedAtMs =
    raw.invalidatedAtMs === null ? null : normalizePositiveInteger(raw.invalidatedAtMs)
  if (raw.invalidatedAtMs !== null && invalidatedAtMs === null) {
    return null
  }

  const clearedThroughSavedAtMs =
    raw.clearedThroughSavedAtMs === null || raw.clearedThroughSavedAtMs === undefined
      ? null
      : normalizePositiveInteger(raw.clearedThroughSavedAtMs)
  if (
    raw.clearedThroughSavedAtMs !== null &&
    raw.clearedThroughSavedAtMs !== undefined &&
    clearedThroughSavedAtMs === null
  ) {
    return null
  }

  const summary = raw.summary === null ? null : normalizeSummary(raw.summary)
  if (
    raw.summary !== null &&
    summary === null &&
    invalidatedAtMs === null &&
    clearedThroughSavedAtMs === null
  ) {
    return null
  }

  if (summary !== null && summary.sessionId !== sessionId) {
    return null
  }

  return {
    sessionId,
    summary,
    invalidatedAtMs,
    clearedThroughSavedAtMs,
  }
}

function isReplicaMetadataMessage(
  value: unknown,
): value is ThreadSyncReplicaMetadataMessage {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false
  }

  const raw = value as Record<string, unknown>
  if (raw.type === "summary_upsert") {
    return normalizeSummary(raw.summary) !== null
  }
  if (raw.type === "invalidation_mark") {
    return (
      normalizeNonEmptyString(raw.sessionId) !== null &&
      normalizePositiveInteger(raw.invalidatedAtMs) !== null
    )
  }
  if (raw.type === "metadata_clear") {
    return normalizeNonEmptyString(raw.sessionId) !== null
  }
  if (raw.type === "summary_clear") {
    return (
      normalizeNonEmptyString(raw.sessionId) !== null &&
      (
        raw.clearedThroughSavedAtMs === null ||
        (
          typeof raw.clearedThroughSavedAtMs === "number" &&
          Number.isFinite(raw.clearedThroughSavedAtMs) &&
          raw.clearedThroughSavedAtMs >= 0
        )
      )
    )
  }
  return false
}

function getRecord(sessionId: string): ThreadSyncPersistedReplicaMetadataRecord {
  return (
    persistedReplicaMetadataBySessionId.get(sessionId) ?? {
      sessionId,
      summary: null,
      invalidatedAtMs: null,
      clearedThroughSavedAtMs: null,
    }
  )
}

function getRecordFreshnessTimestamp(
  record: ThreadSyncPersistedReplicaMetadataRecord,
): number {
  return Math.max(
    typeof record.summary?.savedAtMs === "number" && Number.isFinite(record.summary.savedAtMs)
      ? record.summary.savedAtMs
      : 0,
    record.invalidatedAtMs ?? 0,
    record.clearedThroughSavedAtMs ?? 0,
  )
}

function isMetadataRecordExpired(args: {
  record: ThreadSyncPersistedReplicaMetadataRecord
  nowMs?: number
}): boolean {
  const freshnessTimestamp = getRecordFreshnessTimestamp(args.record)
  if (freshnessTimestamp <= 0) {
    return false
  }

  return freshnessTimestamp + THREAD_SYNC_REPLICA_METADATA_TTL_MS <= (args.nowMs ?? Date.now())
}

function pruneExpiredReplicaMetadata(nowMs = Date.now()): void {
  lastReplicaMetadataPruneAtMs = nowMs
  for (const [sessionId, record] of persistedReplicaMetadataBySessionId.entries()) {
    if (isMetadataRecordExpired({ record, nowMs })) {
      persistedReplicaMetadataBySessionId.delete(sessionId)
    }
  }

  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  const keysToRemove: string[] = []
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key || !key.startsWith(THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX)) continue
    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) {
        keysToRemove.push(key)
        continue
      }
      const record = normalizeMetadataRecord(JSON.parse(raw) as unknown)
      if (!record || isMetadataRecordExpired({ record, nowMs })) {
        keysToRemove.push(key)
      }
    } catch {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    window.localStorage.removeItem(key)
  }
}

function maybePruneExpiredReplicaMetadata(nowMs = Date.now()): void {
  if (nowMs - lastReplicaMetadataPruneAtMs < THREAD_SYNC_REPLICA_METADATA_PRUNE_INTERVAL_MS) {
    return
  }

  pruneExpiredReplicaMetadata(nowMs)
}

function notifySubscribers(change: ThreadSyncPersistedReplicaMetadataChange): void {
  for (const listener of subscribers) {
    listener(change)
  }
}

function mirrorRecordToStorage(record: ThreadSyncPersistedReplicaMetadataRecord): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  maybePruneExpiredReplicaMetadata()
  try {
    window.localStorage.setItem(
      composeReplicaMetadataStorageKey(record.sessionId),
      JSON.stringify(record),
    )
  } catch {
    // Ignore storage failures; same-tab memory and BroadcastChannel may still work.
  }
}

function removeRecordFromStorage(sessionId: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  try {
    window.localStorage.removeItem(composeReplicaMetadataStorageKey(sessionId))
  } catch {
    // Ignore storage failures.
  }
}

function postReplicaMetadataMessage(message: ThreadSyncReplicaMetadataMessage): void {
  try {
    broadcastChannel?.postMessage(message)
  } catch {
    // Ignore BroadcastChannel failures.
  }
}

function upsertSummaryInternal(args: {
  summary: ThreadSyncBackgroundWarmSummary
  mirrorToStorage?: boolean
  broadcast?: boolean
  notify?: boolean
}): void {
  const normalizedSummary = normalizeSummary(args.summary)
  if (normalizedSummary === null) {
    const sessionId = normalizeNonEmptyString(
      (args.summary as unknown as { sessionId?: unknown }).sessionId,
    )
    if (sessionId !== null) {
      clearSummaryInternal({
        sessionId,
        mirrorToStorage: args.mirrorToStorage,
        broadcast: args.broadcast,
        notify: args.notify,
      })
    }
    return
  }

  const existing = getRecord(normalizedSummary.sessionId)
  const summarySavedAtMs =
    getSummarySavedAtMs(normalizedSummary)
  observeReplicaMetadataTimestamp({
    sessionId: normalizedSummary.sessionId,
    timestampMs: summarySavedAtMs,
  })
  if (
    existing.invalidatedAtMs !== null &&
    existing.invalidatedAtMs >= summarySavedAtMs
  ) {
    return
  }
  if (
    existing.clearedThroughSavedAtMs !== null &&
    existing.clearedThroughSavedAtMs >= summarySavedAtMs
  ) {
    return
  }

  if (existing.summary !== null) {
    if (areThreadSyncBackgroundWarmSummariesEquivalent({
      current: existing.summary,
      incoming: normalizedSummary,
    })) {
      return
    }

    if (compareThreadSyncReplicaSummaries({
      current: existing.summary,
      incoming: normalizedSummary,
    }) === "current") {
      return
    }
  }

  const nextRecord: ThreadSyncPersistedReplicaMetadataRecord = {
    sessionId: normalizedSummary.sessionId,
    summary: normalizedSummary,
    invalidatedAtMs: null,
    clearedThroughSavedAtMs: null,
  }
  persistedReplicaMetadataBySessionId.set(normalizedSummary.sessionId, nextRecord)

  if (args.mirrorToStorage !== false) {
    mirrorRecordToStorage(nextRecord)
  }
  if (args.broadcast !== false) {
    postReplicaMetadataMessage({
      type: "summary_upsert",
      summary: normalizedSummary,
    })
  }
  if (args.notify !== false) {
    notifySubscribers({
      type: "summary_upsert",
      sessionId: normalizedSummary.sessionId,
    })
  }
}

function markInvalidationInternal(args: {
  sessionId: string
  invalidatedAtMs: number
  mirrorToStorage?: boolean
  broadcast?: boolean
  notify?: boolean
}): void {
  observeReplicaMetadataTimestamp({
    sessionId: args.sessionId,
    timestampMs: args.invalidatedAtMs,
  })
  const existing = getRecord(args.sessionId)
  const nextInvalidatedAtMs = Math.max(
    existing.invalidatedAtMs ?? 0,
    args.invalidatedAtMs,
  )
  const nextSummary =
    existing.summary !== null &&
    (typeof existing.summary.savedAtMs === "number"
      ? existing.summary.savedAtMs
      : 0) > nextInvalidatedAtMs
      ? existing.summary
      : null
  const nextRecord: ThreadSyncPersistedReplicaMetadataRecord = {
    sessionId: args.sessionId,
    summary: nextSummary,
    invalidatedAtMs: nextInvalidatedAtMs,
    clearedThroughSavedAtMs: Math.max(
      existing.clearedThroughSavedAtMs ?? 0,
      nextInvalidatedAtMs,
    ),
  }
  if (
    nextInvalidatedAtMs === existing.invalidatedAtMs &&
    areOptionalThreadSyncBackgroundWarmSummariesEquivalent({
      current: existing.summary,
      incoming: nextSummary,
    })
  ) {
    return
  }
  persistedReplicaMetadataBySessionId.set(args.sessionId, nextRecord)

  if (args.mirrorToStorage !== false) {
    mirrorRecordToStorage(nextRecord)
  }
  if (args.broadcast !== false) {
    postReplicaMetadataMessage({
      type: "invalidation_mark",
      sessionId: args.sessionId,
      invalidatedAtMs: nextInvalidatedAtMs,
    })
  }
  if (args.notify !== false) {
    notifySubscribers({
      type: "invalidation_mark",
      sessionId: args.sessionId,
      invalidatedAtMs: nextInvalidatedAtMs,
    })
  }
}

function clearMetadataInternal(args: {
  sessionId: string
  mirrorToStorage?: boolean
  broadcast?: boolean
  notify?: boolean
}): void {
  if (!persistedReplicaMetadataBySessionId.has(args.sessionId)) {
    return
  }
  persistedReplicaMetadataBySessionId.delete(args.sessionId)
  if (args.mirrorToStorage !== false) {
    removeRecordFromStorage(args.sessionId)
  }
  if (args.broadcast !== false) {
    postReplicaMetadataMessage({
      type: "metadata_clear",
      sessionId: args.sessionId,
    })
  }
  if (args.notify !== false) {
    notifySubscribers({
      type: "metadata_clear",
      sessionId: args.sessionId,
    })
  }
}

function clearSummaryInternal(args: {
  sessionId: string
  clearedThroughSavedAtMs?: number | null
  mirrorToStorage?: boolean
  broadcast?: boolean
  notify?: boolean
}): void {
  const existing = getRecord(args.sessionId)
  const existingSummarySavedAtMs = getSummarySavedAtMs(existing.summary)
  const existingClearedThroughSavedAtMs = existing.clearedThroughSavedAtMs ?? 0
  const clearedThroughSavedAtMs =
    typeof args.clearedThroughSavedAtMs === "number" && Number.isFinite(args.clearedThroughSavedAtMs)
      ? Math.max(0, Math.floor(args.clearedThroughSavedAtMs))
      : existingSummarySavedAtMs
  observeReplicaMetadataTimestamp({
    sessionId: args.sessionId,
    timestampMs: clearedThroughSavedAtMs,
  })
  if (existing.summary === null && clearedThroughSavedAtMs <= existingClearedThroughSavedAtMs) {
    return
  }
  if (existingSummarySavedAtMs > clearedThroughSavedAtMs) {
    return
  }

  const nextClearedThroughSavedAtMs = Math.max(
    existingClearedThroughSavedAtMs,
    existingSummarySavedAtMs,
    clearedThroughSavedAtMs,
  )

  const nextRecord: ThreadSyncPersistedReplicaMetadataRecord = {
    sessionId: args.sessionId,
    summary: null,
    invalidatedAtMs: existing.invalidatedAtMs,
    clearedThroughSavedAtMs: nextClearedThroughSavedAtMs > 0
      ? nextClearedThroughSavedAtMs
      : null,
  }
  if (
    existing.summary === null &&
    existing.invalidatedAtMs === nextRecord.invalidatedAtMs &&
    existing.clearedThroughSavedAtMs === nextRecord.clearedThroughSavedAtMs
  ) {
    return
  }
  persistedReplicaMetadataBySessionId.set(args.sessionId, nextRecord)

  if (args.mirrorToStorage !== false) {
    mirrorRecordToStorage(nextRecord)
  }
    if (args.broadcast !== false) {
      postReplicaMetadataMessage({
        type: "summary_clear",
        sessionId: args.sessionId,
        clearedThroughSavedAtMs,
      })
    }
    if (args.notify !== false) {
      notifySubscribers({
        type: "summary_clear",
        sessionId: args.sessionId,
        clearedThroughSavedAtMs,
      })
    }
}

function handleStorageEvent(event: StorageEvent): void {
  if (!event.key) return
  const sessionId = parseReplicaMetadataStorageKey(event.key)
  if (!sessionId) return

  if (event.newValue === null) {
    clearMetadataInternal({
      sessionId,
      mirrorToStorage: false,
      broadcast: false,
    })
    return
  }

  try {
    const parsed = JSON.parse(event.newValue) as unknown
    const record = normalizeMetadataRecord(parsed)
    if (!record) {
      clearMetadataInternal({
        sessionId,
        mirrorToStorage: false,
        broadcast: false,
      })
      try {
        window.localStorage.removeItem(event.key)
      } catch {
        // Ignore cleanup failures.
      }
      return
    }
    if (
      record.summary === null &&
      record.invalidatedAtMs === null &&
      record.clearedThroughSavedAtMs === null
    ) {
      clearMetadataInternal({
        sessionId: record.sessionId,
        mirrorToStorage: false,
        broadcast: false,
      })
      try {
        window.localStorage.removeItem(event.key)
      } catch {
        // Ignore cleanup failures.
      }
      return
    }
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed) &&
      "summary" in parsed &&
      (parsed as { summary?: unknown }).summary !== null &&
      record.summary === null
    ) {
      try {
        window.localStorage.setItem(event.key, JSON.stringify(record))
      } catch {
        // Ignore cleanup rewrite failures.
      }
    }
    if (record.invalidatedAtMs !== null) {
      markInvalidationInternal({
        sessionId: record.sessionId,
        invalidatedAtMs: record.invalidatedAtMs,
        mirrorToStorage: false,
        broadcast: false,
      })
    }
    if (record.summary !== null) {
      upsertSummaryInternal({
        summary: record.summary,
        mirrorToStorage: false,
        broadcast: false,
      })
      return
    }
    if (record.clearedThroughSavedAtMs !== null) {
      clearSummaryInternal({
        sessionId: record.sessionId,
        clearedThroughSavedAtMs: record.clearedThroughSavedAtMs,
        mirrorToStorage: false,
        broadcast: false,
      })
      return
    }
    if (record.invalidatedAtMs !== null) {
      return
    }
    clearMetadataInternal({
      sessionId: record.sessionId,
      mirrorToStorage: false,
      broadcast: false,
    })
  } catch {
    // Ignore malformed mirrored metadata.
  }
}

function handleBroadcastMessage(event: MessageEvent<unknown>): void {
  const message = event.data
  if (!isReplicaMetadataMessage(message)) return

  if (message.type === "summary_upsert") {
    upsertSummaryInternal({
      summary: message.summary,
      mirrorToStorage: false,
      broadcast: false,
    })
    return
  }

  if (message.type === "invalidation_mark") {
    markInvalidationInternal({
      sessionId: message.sessionId,
      invalidatedAtMs: message.invalidatedAtMs,
      mirrorToStorage: false,
      broadcast: false,
    })
    return
  }

  if (message.type === "summary_clear") {
    clearSummaryInternal({
      sessionId: message.sessionId,
      clearedThroughSavedAtMs: message.clearedThroughSavedAtMs,
      mirrorToStorage: false,
      broadcast: false,
    })
    return
  }

  clearMetadataInternal({
    sessionId: message.sessionId,
    mirrorToStorage: false,
    broadcast: false,
  })
}

function bootstrapMirroredMetadata(): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  pruneExpiredReplicaMetadata()
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key || !key.startsWith(THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX)) continue
    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw) as unknown
      const record = normalizeMetadataRecord(parsed)
      if (!record) {
        window.localStorage.removeItem(key)
        continue
      }
      if (
        record.summary === null &&
        record.invalidatedAtMs === null &&
        record.clearedThroughSavedAtMs === null
      ) {
        window.localStorage.removeItem(key)
        continue
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed) &&
        "summary" in parsed &&
        (parsed as { summary?: unknown }).summary !== null &&
        record.summary === null
      ) {
        try {
          window.localStorage.setItem(key, JSON.stringify(record))
        } catch {
          // Ignore cleanup rewrite failures.
        }
      }
      persistedReplicaMetadataBySessionId.set(record.sessionId, record)
    } catch {
      // Ignore malformed bootstrap entries.
    }
  }
}

function initializeReplicaMetadataStore(): void {
  if (initialized || typeof window === "undefined") return
  initialized = true
  bootstrapMirroredMetadata()
  if (typeof window.BroadcastChannel !== "undefined") {
    try {
      broadcastChannel = new window.BroadcastChannel(
        THREAD_SYNC_REPLICA_METADATA_BROADCAST_CHANNEL,
      )
      broadcastChannel.addEventListener("message", handleBroadcastMessage)
    } catch {
      broadcastChannel = null
    }
  }
  if (typeof window.addEventListener === "function") {
    window.addEventListener("storage", handleStorageEvent)
  }
}

export function getThreadSyncPersistedReplicaSummary(
  sessionId: string,
): ThreadSyncBackgroundWarmSummary | null {
  initializeReplicaMetadataStore()
  return persistedReplicaMetadataBySessionId.get(sessionId)?.summary ?? null
}

export function getThreadSyncPersistedReplicaInvalidation(
  sessionId: string,
): number | null {
  initializeReplicaMetadataStore()
  return persistedReplicaMetadataBySessionId.get(sessionId)?.invalidatedAtMs ?? null
}

export function subscribeThreadSyncPersistedReplicaMetadata(
  listener: (change: ThreadSyncPersistedReplicaMetadataChange) => void,
): () => void {
  initializeReplicaMetadataStore()
  subscribers.add(listener)
  return () => {
    subscribers.delete(listener)
  }
}

export function upsertThreadSyncPersistedReplicaSummary(
  summary: ThreadSyncBackgroundWarmSummary,
): void {
  initializeReplicaMetadataStore()
  const normalizedSummary = normalizeSummary(summary)
  upsertSummaryInternal({
    summary: normalizedSummary === null
      ? summary
      : reserveReplicaSummarySavedAtMs(normalizedSummary),
  })
}

export function markThreadSyncPersistedReplicaInvalidation(args: {
  sessionId: string
  invalidatedAtMs: number
}): void {
  initializeReplicaMetadataStore()
  markInvalidationInternal(args)
}

export function clearThreadSyncPersistedReplicaSummary(sessionId: string): void {
  initializeReplicaMetadataStore()
  clearMetadataInternal({ sessionId })
}

export function clearThreadSyncPersistedReplicaSummaryPreservingInvalidation(
  sessionId: string,
  clearedThroughSavedAtMs?: number | null,
): void {
  initializeReplicaMetadataStore()
  clearSummaryInternal({ sessionId, clearedThroughSavedAtMs })
}

export function clearAllThreadSyncPersistedReplicaSummaries(): void {
  persistedReplicaMetadataBySessionId.clear()
  replicaMetadataClockBySessionId.clear()
  lastReplicaMetadataPruneAtMs = 0
  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    const keysToRemove: string[] = []
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (!key || !key.startsWith(THREAD_SYNC_REPLICA_METADATA_STORAGE_PREFIX)) continue
      keysToRemove.push(key)
    }
    for (const key of keysToRemove) {
      window.localStorage.removeItem(key)
    }
  }
}
